import React, { useState, useRef, useEffect } from "react";
import { format, isValid, parse } from "date-fns";
import ButtonIcon from "../../ButtonIcon/ButtonIcon";
import CaretDown from "../../../assets/svg/icons/common/CaretDown";
import CaretRight from "../../../assets/svg/icons/common/CaretRight";
import CaretLeft from "../../../assets/svg/icons/common/CaretLeft";
import Calendar from "../../../assets/svg/icons/common/Calender";
import formBaseStyles from "../text-input/styles.module.scss";
import styles from "./styles.module.scss";
import useDomainStore from "../../../stores/domain";

const yearFilters = [
  {
    id: 1,
    buttonLabel: "MTD",
    displayLabel: "Month to date",
    onClick: () => {},
  },
  {
    id: 2,
    buttonLabel: "LTY",
    displayLabel: "Last Year",
    onClick: () => {},
  },
  {
    id: 3,
    buttonLabel: "YTD",
    displayLabel: "Year to date",
  },
];

export const MonthPicker = ({
  id,
  label,
  placeholder = "Select period",
  value,
  onChange,
  onSubmit,
  variant = "default",
  dropdownHeader = "",
  showActions = false,
  changeOnSubmit = false,
  showYearFilters = false,
  defaultDate,
  onOpenChange,
  supportMobile = false,
}) => {
  const { isKurvDomain } = useDomainStore();
  const currentYear = new Date().getFullYear();
  const currentMonthIndex = new Date().getMonth();
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const displayMonthLabel = (abbrev) => {
    const parsed = parse(abbrev, "MMM", new Date());
    return isValid(parsed) ? format(parsed, "MMMM") : abbrev;
  };

  // Determine initial month and year based on defaultDate prop or current date
  const getInitialDate = () => {
    if (defaultDate) {
      const date = defaultDate instanceof Date ? defaultDate : new Date(defaultDate);
      if (!isNaN(date.getTime())) {
        return {
          year: date.getFullYear(),
          month: months[date.getMonth()],
        };
      }
    }
    return {
      year: currentYear,
      month: months[currentMonthIndex],
    };
  };

  const initialDate = getInitialDate();
  const selectContainerRef = useRef(null);
  const onOpenChangeRef = useRef(onOpenChange);
  onOpenChangeRef.current = onOpenChange;
  const [tempSelectedYear, setTempSelectedYear] = useState(initialDate.year);
  const [tempSelectedMonth, setTempSelectedMonth] = useState(initialDate.month);
  const [selectedYear, setSelectedYear] = useState(initialDate.year);
  const [selectedMonth, setSelectedMonth] = useState(initialDate.month);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const decreaseYear = () => {
    const yearToDecrease = changeOnSubmit ? tempSelectedYear : selectedYear;
    if (changeOnSubmit) {
      setTempSelectedYear(yearToDecrease - 1);
    } else {
      setSelectedYear(yearToDecrease - 1);
      if (onChange) {
        onChange(selectedMonth, yearToDecrease - 1);
      }
    }
  };

  const increaseYear = () => {
    const yearToIncrease = changeOnSubmit ? tempSelectedYear : selectedYear;
    if (yearToIncrease + 1 <= currentYear) {
      if (changeOnSubmit) {
        setTempSelectedYear(yearToIncrease + 1);
      } else {
        setSelectedYear(yearToIncrease + 1);
        if (onChange) {
          onChange(selectedMonth, yearToIncrease + 1);
        }
      }
    }
  };

  const handleMonthSelect = (month) => {
    if (changeOnSubmit) {
      setTempSelectedMonth(month);
    } else {
      setSelectedMonth(month);
      if (onChange) {
        onChange(month, selectedYear);
      }
    }
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        selectContainerRef.current &&
        !selectContainerRef.current.contains(e.target)
      ) {
        setDropdownOpen((prev) => {
          if (prev) {
            onOpenChangeRef.current?.(false);
          }
          return false;
        });
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div
      className={`${styles.selectContainer} ${variant ? styles[variant] : ""} ${isKurvDomain ? styles.kurv_theme : ""} ${supportMobile ? styles.supportMobile : ""}`}
      ref={selectContainerRef}
    >
      {label && <label className={formBaseStyles.label}>{label}</label>}
      <div
        className={`${formBaseStyles.input} ${styles.input}`}
        tabIndex={0}
        onClick={() => {
          const next = !dropdownOpen;
          setDropdownOpen(next);
          onOpenChangeRef.current?.(next);
        }}
        onKeyUp={(e) => {
          if (e.key === "Enter") {
            const next = !dropdownOpen;
            setDropdownOpen(next);
            onOpenChangeRef.current?.(next);
          }
        }}
      >
        <Calendar className={styles.calendarIcon} />

        {yearFilters.map((f) => f.buttonLabel).indexOf(selectedMonth) > -1 ? (
          <p className={styles.inputSelection}>
            {
              yearFilters.find((f) => f.buttonLabel === selectedMonth)
                .displayLabel
            }
          </p>
        ) : (
          <p className={styles.inputSelection}>
            {!selectedMonth && placeholder}
            {selectedMonth &&
              (supportMobile
                ? `${displayMonthLabel(selectedMonth)} ${selectedYear}`
                : `${selectedMonth} ${selectedYear}`)}
          </p>
        )}

        <div className={`${styles.caret} ${dropdownOpen && styles.open}`}>
          <CaretDown />
        </div>
      </div>
      {dropdownOpen && (
        <div className={styles.pickerContainer}>
          <div className={styles.header}>
            <button className={styles.navBtn} onClick={decreaseYear}>
              <CaretLeft />
            </button>
            <span className={styles.year}>
              {changeOnSubmit ? tempSelectedYear : selectedYear}
            </span>
            <button
              className={`${styles.navBtn} ${
                changeOnSubmit
                  ? tempSelectedYear === currentYear
                    ? styles.disabled
                    : ""
                  : selectedYear === currentYear
                    ? styles.disabled
                    : ""
              }`}
              onClick={increaseYear}
            >
              <CaretRight />
            </button>
          </div>

          <div className={styles.monthsGrid}>
            {months.map((month) => (
              <button
                key={month}
                onClick={() => handleMonthSelect(month)}
                className={`${styles.monthBtn} ${
                  changeOnSubmit
                    ? tempSelectedMonth === month
                      ? styles.selected
                      : ""
                    : selectedMonth === month
                      ? styles.selected
                      : ""
                }`}
              >
                {month}
              </button>
            ))}
          </div>

          {showYearFilters && (
            <div className={styles.yearFiltersContainer}>
              {yearFilters.map((action) => (
                <div
                  key={action.id}
                  onClick={() => {
                    if (changeOnSubmit) {
                      setTempSelectedMonth(action.buttonLabel);
                    } else {
                      setSelectedMonth(action.buttonLabel);
                    }
                  }}
                  className={`${styles.yearFilter} ${
                    changeOnSubmit
                      ? tempSelectedMonth === action.buttonLabel
                        ? styles.active
                        : ""
                      : selectedMonth === action.buttonLabel
                        ? styles.active
                        : ""
                  }`}
                >
                  {action.buttonLabel}
                </div>
              ))}
            </div>
          )}

          {showActions && (
            <div className={styles.actions}>
              <ButtonIcon
                onClick={() => {
                  setDropdownOpen(false);
                  onOpenChangeRef.current?.(false);
                }}
                className={styles.actionButton}
              >
                Cancel
              </ButtonIcon>
              <ButtonIcon
                bgColor={"#4C70EF"}
                textColor="#FFF"
                onClick={() => {
                  if (changeOnSubmit) {
                    setSelectedYear(tempSelectedYear);
                    setSelectedMonth(tempSelectedMonth);
                  }

                  onSubmit({
                    year: changeOnSubmit ? tempSelectedYear : selectedYear,
                    month: changeOnSubmit ? tempSelectedMonth : selectedMonth,
                  });
                  setDropdownOpen(false);
                  onOpenChangeRef.current?.(false);
                }}
                className={styles.actionButton}
              >
                Submit
              </ButtonIcon>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MonthPicker;
